import React, { useEffect, useState } from "react";
import { Grid3x3, Info, ChevronRight } from "lucide-react";
import { api, fmtDec, fmtInt, fmtMWh } from "../lib/api";
import Semaforo from "./Semaforo";

/**
 * Matriz comparativa de consumo kWh/km de la línea con tipologías equivalentes o superiores.
 * Implementa el "ajuste de alcance" del contrato:
 *   Clase I 12 m, 15 m, 18 m + Clase II 12 m, 15 m, 18 m + Clase III, etc.
 */
export default function MatrizTipologias({ codigoLinea }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    api
      .get(`/lineas/${encodeURIComponent(codigoLinea)}/tipologias_alternativas`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.message || "Error"); });
    return () => { cancelled = true; };
  }, [codigoLinea]);

  if (error) return (
    <div className="crtm-card p-5 text-sm text-red-700" data-testid="matriz-error">
      No se pudo cargar la matriz: {error}
    </div>
  );
  if (!data) return (
    <div className="crtm-card p-5 text-sm text-gray-500" data-testid="matriz-loading">
      Cargando matriz de tipologías…
    </div>
  );

  // Agrupar por tamaño para visualización tabular más limpia
  const tamanos = ["8 m", "10 m", "12 m", "15 m", "18 m"];
  const clases = ["Clase A", "Clase I", "Clase II", "Clase III"];
  // Mapa para acceso rápido
  const cellMap = {};
  data.matriz.forEach((c) => { cellMap[`${c.clase}|${c.tamano}`] = c; });

  const validas = data.matriz.filter((m) => m.valida);
  const mejor = [...validas].sort((a, b) => a.factor_kwh_km_base - b.factor_kwh_km_base)[0];

  return (
    <div className="crtm-card p-6" data-testid="matriz-tipologias">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
            <Grid3x3 size={15} strokeWidth={1.75} className="text-gray-700" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-[15px]">Matriz de tipologías alternativas</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Consumo kWh/km comparado con tipologías equivalentes o superiores, preservando las condiciones operativas y orográficas de la línea.
            </p>
          </div>
        </div>
        <span className="text-[10px] tracking-wider text-gray-400 uppercase font-semibold whitespace-nowrap">
          Ajuste de alcance · Propuesta
        </span>
      </div>

      {/* Tabla por filas (tamaño) y columnas (clase) */}
      <div className="overflow-x-auto">
        <table className="crtm-table" data-testid="matriz-tipologias-table">
          <thead>
            <tr>
              <th className="text-left">Tamaño</th>
              {clases.map((c) => (
                <th key={c} className="text-center">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tamanos.map((t) => (
              <tr key={t}>
                <td className="font-semibold">{t}</td>
                {clases.map((c) => {
                  const cell = cellMap[`${c}|${t}`];
                  if (!cell) {
                    return <td key={c} className="text-center text-gray-300">—</td>;
                  }
                  return (
                    <td key={c} className="text-center align-middle" data-testid={`matriz-cell-${c.replace(/\s/g,'_')}-${t.replace(/\s/g,'_')}`}>
                      <Cell cell={cell} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Resumen / leyenda */}
      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
        <SummaryBox
          tone="emerald"
          label="Tipología actual (Anexo VIII)"
          title={data.tipologia_actual}
          line1={`${fmtDec(data.consumo_actual_kwh_km, 2)} kWh/km base`}
          line2={`${fmtDec(data.consumo_actual_conservador_kwh_km, 2)} kWh/km conservador`}
        />
        {mejor && (
          <SummaryBox
            tone="blue"
            label="Tipología válida más eficiente"
            title={mejor.tipologia}
            line1={`${fmtDec(mejor.factor_kwh_km_base, 2)} kWh/km base`}
            line2={`${mejor.delta_factor_pct > 0 ? "+" : ""}${fmtDec(mejor.delta_factor_pct, 1)}% vs. tipología actual`}
          />
        )}
        <SummaryBox
          tone="amber"
          label="Cobertura"
          title={`${validas.length} alternativas válidas`}
          line1={`${data.matriz.length - validas.length} descartadas por restricciones operativas`}
          line2={`Servicio: ${data.tipo_servicio}`}
        />
      </div>

      <div className="mt-4 text-[11px] text-gray-500 leading-relaxed flex gap-2 items-start">
        <Info size={13} className="text-gray-400 mt-0.5 flex-shrink-0" />
        <span>
          <strong>Metodología:</strong> el factor de la línea actual se descompone en una componente base media de su tipología
          (calibrada con el dataset) y un sesgo específico de la línea (orografía, urbanidad, paradas). Las alternativas se
          calculan aplicando ese sesgo a la base media de cada (clase, tamaño). Las celdas marcadas como
          no válidas violan reglas operativas (tamaño inferior, clase incompatible con el tipo de servicio o catálogo inexistente).
        </span>
      </div>
    </div>
  );
}

const Cell = ({ cell }) => {
  const VIAB_BG = {
    "Alta":         "bg-green-50 border-green-200",
    "Condicionada": "bg-amber-50 border-amber-200",
    "A revisar":    "bg-red-50 border-red-200",
  };
  const bg = cell.valida ? (VIAB_BG[cell.viabilidad] || "bg-gray-50 border-gray-200") : "bg-gray-100 border-dashed border-gray-300";
  const opacity = cell.valida ? "" : "opacity-50";
  const ring = cell.es_actual ? "ring-2 ring-emerald-700 ring-offset-1" : "";
  return (
    <div className={`inline-block min-w-[110px] rounded-md border px-2.5 py-1.5 ${bg} ${opacity} ${ring}`} title={cell.motivo_no_valida || cell.viabilidad}>
      {cell.es_actual && (
        <div className="text-[9px] uppercase tracking-wider font-bold text-emerald-700 mb-0.5">Actual</div>
      )}
      <div className="font-mono font-bold text-sm text-gray-900 leading-tight">
        {fmtDec(cell.factor_kwh_km_base, 2)}
      </div>
      <div className="text-[10px] text-gray-500 font-mono">cons. {fmtDec(cell.factor_kwh_km_conservador, 2)}</div>
      <div className="mt-1 flex items-center justify-center gap-1">
        {cell.valida ? (
          <>
            <Semaforo viabilidad={cell.viabilidad} compact />
            <span className={`text-[10px] font-mono font-semibold ${cell.delta_factor_pct > 0 ? "text-red-700" : cell.delta_factor_pct < 0 ? "text-green-700" : "text-gray-600"}`}>
              {cell.delta_factor_pct > 0 ? "+" : ""}{fmtDec(cell.delta_factor_pct, 1)}%
            </span>
          </>
        ) : (
          <span className="text-[10px] text-gray-400 italic">no aplicable</span>
        )}
      </div>
    </div>
  );
};

const SummaryBox = ({ tone, label, title, line1, line2 }) => {
  const toneMap = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    blue:    "border-blue-200 bg-blue-50 text-blue-900",
    amber:   "border-amber-200 bg-amber-50 text-amber-900",
  };
  return (
    <div className={`rounded-lg border p-3 ${toneMap[tone] || ""}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{label}</div>
      <div className="font-heading font-semibold text-sm mt-1 flex items-center gap-1">
        <ChevronRight size={14} /> {title}
      </div>
      <div className="text-xs mt-1 font-mono">{line1}</div>
      {line2 && <div className="text-[11px] mt-0.5 opacity-80">{line2}</div>}
    </div>
  );
};
